import { useState, useRef, useEffect } from 'react';
import { AdjustmentsVerticalIcon } from '@heroicons/react/24/outline';

interface AlphaSliderProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
}

export default function AlphaSlider({
  value,
  onChange,
  disabled = false,
  min = 0,
  max = 1,
  step = 0.1,
  label = '情感强度'
}: AlphaSliderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sliderRef.current && !sliderRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={sliderRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center gap-1 px-2 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isOpen
          ? 'bg-blue-600 text-white'
          : 'bg-bg-elevated text-gray-400 hover:bg-border-default hover:text-gray-300 border border-border-default'
          }`}
        title={label}
      >
        <AdjustmentsVerticalIcon className="h-4 w-4" />
        <span className="text-xs">{value.toFixed(1)}</span>
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg z-50 p-3">
          <div className="flex flex-col items-center">
            <span className="text-xs text-gray-500 mb-1">{max}</span>
            <div className="relative h-32 w-6 flex items-center justify-center">
              <div className="absolute h-full w-1.5 bg-border-default rounded-full" />
              <div
                className="absolute bottom-0 w-1.5 bg-blue-500 rounded-full transition-all"
                style={{ height: `${((value - min) / (max - min)) * 100}%` }}
              />
              <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                disabled={disabled}
                className="absolute h-full w-full opacity-0 cursor-pointer"
                style={{
                  writingMode: 'vertical-lr',
                  direction: 'rtl'
                }}
              />
              <div
                className="absolute w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-md pointer-events-none"
                style={{
                  bottom: `calc(${((value - min) / (max - min)) * 100}% - 8px)`
                }}
              />
            </div>
            <span className="text-xs text-gray-500 mt-1">{min}</span>
          </div>
          <div className="text-center mt-2">
            <span className="text-sm text-blue-400 font-medium">{value.toFixed(1)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
